import { useState } from 'react';
import { Check, Paperclip, Camera } from 'lucide-react';
import { ALLOWED_DOCUMENT_MIME_TYPES } from '@cumpli/shared';
import { DOC_CATEGORIES, type Persona } from '../lib/phase1';
import { Label, cn } from './ui';

const ACCEPT = ALLOWED_DOCUMENT_MIME_TYPES.join(',');

export interface UploadedDoc {
  category: string;
  name: string;
}

interface DocUploadFlowProps {
  persona: Persona;
  attached: UploadedDoc[];
  /** Uploads the chosen file (resolves on success; should handle its own errors). */
  onUpload: (file: File, category: string) => Promise<void>;
}

/** Category chip, then a single file drop — one decision, then one action. */
export function DocUploadFlow({ persona, attached, onUpload }: DocUploadFlowProps) {
  const categories = DOC_CATEGORIES[persona];
  const [category, setCategory] = useState(categories[0] ?? '');
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  async function handleFile(file: File) {
    setBusy(true);
    try {
      await onUpload(file, category);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-4">
      <div>
        <Label>Categoría del documento</Label>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              className={cn(
                'rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors',
                category === c
                  ? 'border-side bg-side text-white'
                  : 'border-line-strong bg-panel text-ink-soft hover:border-ink-faint'
              )}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files[0];
          if (file && !busy) void handleFile(file);
        }}
        className={cn(
          'grid cursor-pointer place-items-center gap-1 rounded-xl border-2 border-dashed px-4 py-7 text-center transition-colors hover:border-primary',
          dragOver ? 'border-primary bg-primary/5' : 'border-line-strong bg-canvas',
          busy && 'pointer-events-none opacity-60'
        )}
      >
        <input
          type="file"
          accept={ACCEPT}
          className="sr-only"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            e.target.value = '';
          }}
        />
        {busy ? (
          <>
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-line-strong border-t-primary" />
            <span className="text-sm font-semibold text-ink-soft">Subiendo…</span>
          </>
        ) : (
          <>
            <Paperclip className="h-6 w-6 text-ink-faint" strokeWidth={1.75} />
            <span className="text-sm font-semibold text-ink-soft">Arrastra un documento o haz clic</span>
            <span className="font-mono text-[10px] text-ink-faint">categoría: {category}</span>
          </>
        )}
      </label>

      {/* camera capture — opens the camera on mobile, file picker on desktop */}
      <label
        className={cn(
          'flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-line-strong bg-panel py-3.5 text-sm font-bold text-ink-soft transition-colors hover:border-primary hover:text-primary-dark',
          busy && 'pointer-events-none opacity-60'
        )}
      >
        <input
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            e.target.value = '';
          }}
        />
        <Camera className="h-5 w-5" strokeWidth={1.75} /> Tomar foto
      </label>

      {attached.length > 0 && (
        <div className="grid gap-2">
          {attached.map((d, i) => (
            <div
              key={i}
              className="flex items-center gap-3 rounded-lg border border-line bg-panel px-3.5 py-2.5"
            >
              <Check className="h-4 w-4 text-primary-dark" strokeWidth={2} />
              <span className="text-sm font-bold">{d.name}</span>
              <span className="ml-auto font-mono text-[10px] text-ink-faint">{d.category}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
